import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Marquee from 'react-fast-marquee';
import { Calendar, MapPin, ArrowUpRight, Loader2 } from 'lucide-react';
import { BRANDS } from './data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
const MONTHS_SHORT = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const parseDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m: m - 1, d };
};

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/events`)
      .then((res) => setEvents(res.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  // Group by month-year preserving date order
  const groups = [];
  const sorted = events.slice().sort((a, b) => a.date.localeCompare(b.date));
  sorted.forEach((ev) => {
    const { y, m } = parseDate(ev.date);
    const key = `${y}-${m}`;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: `${MONTHS[m]} ${y}`, items: [] };
      groups.push(g);
    }
    g.items.push(ev);
  });

  return (
    <section id="eventos" data-testid="events-section" className="relative py-20 sm:py-32 border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://customer-assets.emergentagent.com/job_dj-alfredj/artifacts/t6sizn0x_alfredj3.webp"
          alt=""
          aria-hidden
          className="w-full h-full object-cover opacity-[0.12] saturate-[0.6]"
        />
        <div className="absolute inset-0 bg-ink/80" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-acid uppercase tracking-[0.3em] text-xs mb-4">Agenda</p>
          <h2 className="font-heading uppercase text-4xl sm:text-6xl tracking-tight text-[#f1f0ea]">
            Próximos <span className="text-acid">eventos</span>
          </h2>
          <p className="mt-5 text-[#f1f0ea]/70 font-light max-w-2xl">
            Dónde y cuándo pincha Alfredj. Consulta la agenda por meses y no te pierdas una sesión.
          </p>
        </motion.div>

        {loading && (
          <div className="flex items-center gap-3 text-[#f1f0ea]/60 py-10" data-testid="events-loading">
            <Loader2 className="animate-spin" /> Cargando agenda…
          </div>
        )}
        {!loading && groups.length === 0 && (
          <div data-testid="events-empty" className="border border-white/10 bg-surface p-8 text-[#f1f0ea]/70">
            No hay eventos publicados por ahora. Vuelve pronto o{' '}
            <a href="#contacto" className="text-acid underline">escríbeme para una fecha</a>.
          </div>
        )}
        {!loading && groups.length > 0 && (
          <div className="space-y-14">
            {groups.map((g) => (
              <div key={g.key} data-testid={`events-month-${g.key}`}>
                <h3 className="font-heading uppercase text-2xl sm:text-3xl text-[#f1f0ea]/50 mb-5 flex items-center gap-3">
                  <Calendar size={22} className="text-acid" />
                  {g.label}
                </h3>
                <div className="border-t border-white/10">
                  {g.items.map((ev) => {
                    const { m, d } = parseDate(ev.date);
                    return (
                      <motion.div
                        key={ev.id}
                        data-testid={`event-${ev.id}`}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                        className="group grid grid-cols-[auto_1fr] sm:grid-cols-[90px_1fr_auto] gap-4 sm:gap-8 items-center py-6 border-b border-white/10 hover:bg-white/[0.02] transition-colors"
                      >
                        <div className="flex flex-col items-center justify-center border border-white/15 group-hover:border-acid transition-colors w-[70px] h-[70px] sm:w-[90px] sm:h-[90px]">
                          <span className="font-heading text-3xl sm:text-4xl text-[#f1f0ea] leading-none">
                            {String(d).padStart(2, '0')}
                          </span>
                          <span className="text-xs uppercase tracking-widest text-acid mt-1">
                            {MONTHS_SHORT[m]}
                          </span>
                        </div>

                        <div className="min-w-0">
                          <span className="inline-block text-[10px] uppercase tracking-widest bg-acid/15 text-acid px-2 py-0.5 mb-2">
                            {ev.type}
                          </span>
                          <h4 className="font-heading uppercase text-xl sm:text-3xl text-[#f1f0ea] leading-none truncate">
                            {ev.title}
                          </h4>
                          <p className="mt-2 text-sm sm:text-base text-[#f1f0ea]/60 flex items-center gap-1.5">
                            <MapPin size={15} className="text-acid shrink-0" />
                            {ev.venue} · {ev.city}
                          </p>
                        </div>

                        <div className="col-span-2 sm:col-span-1 sm:justify-self-end">
                          {ev.ticketUrl ? (
                            <a
                              href={ev.ticketUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`event-tickets-${ev.id}`}
                              className="inline-flex items-center gap-1.5 bg-acid text-white font-heading uppercase text-sm px-5 py-3 hover:-translate-y-0.5 transition-transform"
                            >
                              Entradas <ArrowUpRight size={16} />
                            </a>
                          ) : (
                            <a
                              href="#contacto"
                              data-testid={`event-info-${ev.id}`}
                              className="inline-flex items-center gap-1.5 border border-white/20 text-[#f1f0ea] font-heading uppercase text-sm px-5 py-3 hover:border-acid hover:text-acid transition-colors"
                            >
                              Info <ArrowUpRight size={16} />
                            </a>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="relative z-10 mt-16 border-y border-white/10 py-8 overflow-hidden">
        <p className="text-center text-xs uppercase tracking-[0.3em] text-[#f1f0ea]/40 mb-6">
          Salas · Festivales · Marcas
        </p>
        <Marquee speed={35} gradient={false}>
          {BRANDS.concat(BRANDS).map((b, i) => (
            <span key={`${b}-${i}`} className="mx-10 font-heading uppercase text-2xl text-[#f1f0ea]/40 hover:text-acid transition-colors">
              {b}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};
